import { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "../ui/table";
import api, { eur } from "../../lib/api";

const MONTHS = ["gen", "feb", "mar", "apr", "mag", "giu", "lug", "ago", "set", "ott", "nov", "dic"];
const fmtMonth = (m) => { const [y, mm] = (m || "").split("-"); return mm ? `${MONTHS[Number(mm) - 1]} ${y.slice(2)}` : m; };

export default function AnalyticsPanel() {
  const [data, setData] = useState({ monthly: [], per_agent: [] });
  const [kegs, setKegs] = useState([]);

  useEffect(() => {
    api.get("/analytics").then(({ data }) => setData(data)).catch(() => {});
    api.get("/kegs/analytics").then(({ data }) => setKegs(data.monthly || [])).catch(() => {});
  }, []);

  const monthly = data.monthly.map((m) => ({ ...m, label: fmtMonth(m.month) }));

  return (
    <div className="space-y-8">
      <div className="bg-white rounded-2xl border border-stone-200 p-6">
        <h3 className="font-serif text-xl font-semibold text-[#0B2A45] mb-4">Vendite e provvigioni per mese</h3>
        {monthly.length === 0 ? <p className="text-sm text-stone-400 py-8 text-center">Nessun dato ancora</p> : (
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={monthly} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#78716c" }} />
              <YAxis tick={{ fontSize: 12, fill: "#78716c" }} />
              <Tooltip formatter={(v) => eur(v)} contentStyle={{ borderRadius: 12, border: "1px solid #E7E5E4" }} />
              <Legend />
              <Bar dataKey="sales" name="Vendite" fill="#0F4C81" radius={[6, 6, 0, 0]} />
              <Bar dataKey="commission" name="Provvigioni" fill="#EAB308" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-stone-200 p-6">
        <h3 className="font-serif text-xl font-semibold text-[#0B2A45] mb-4">Vuoto a rendere e CO₂ risparmiata</h3>
        {kegs.length === 0 ? <p className="text-sm text-stone-400 py-8 text-center">Nessun dato</p> : (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={kegs.map((m) => ({ ...m, label: fmtMonth(m.month) }))} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#78716c" }} />
              <YAxis tick={{ fontSize: 12, fill: "#78716c" }} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E7E5E4" }} />
              <Legend />
              <Bar dataKey="delivered" name="Consegnati" fill="#0F4C81" radius={[6, 6, 0, 0]} />
              <Bar dataKey="returned" name="Resi" fill="#15803D" radius={[6, 6, 0, 0]} />
              <Bar dataKey="co2" name="CO₂ (kg)" fill="#EAB308" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl border border-stone-200 p-6">
          <h3 className="font-serif text-xl font-semibold text-[#0B2A45] mb-4">Vendite per agente</h3>
          {data.per_agent.length === 0 ? <p className="text-sm text-stone-400 py-8 text-center">Nessun dato</p> : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={data.per_agent} layout="vertical" margin={{ top: 8, right: 16, left: 8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 12, fill: "#78716c" }} />
                <YAxis type="category" dataKey="agent" width={110} tick={{ fontSize: 12, fill: "#78716c" }} />
                <Tooltip formatter={(v) => eur(v)} contentStyle={{ borderRadius: 12, border: "1px solid #E7E5E4" }} />
                <Bar dataKey="sales" name="Vendite" fill="#C2593F" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
        <div className="bg-white rounded-2xl border border-stone-200 p-6">
          <h3 className="font-serif text-xl font-semibold text-[#0B2A45] mb-4">Riepilogo agenti</h3>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>Agente</TableHead><TableHead>Ordini</TableHead><TableHead>Vendite</TableHead><TableHead>Provvigioni</TableHead></TableRow></TableHeader>
              <TableBody>
                {data.per_agent.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-stone-400 py-6">Nessun dato</TableCell></TableRow>}
                {data.per_agent.map((a) => (
                  <TableRow key={a.agent}>
                    <TableCell className="font-medium">{a.agent}</TableCell>
                    <TableCell className="font-mono">{a.orders}</TableCell>
                    <TableCell className="font-mono text-[#0F4C81]">{eur(a.sales)}</TableCell>
                    <TableCell className="font-mono text-[#15803D]">{eur(a.commission)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    </div>
  );
}
